import { useEffect, useState, useMemo } from "react";
import {
  LockKeyhole,
  LogOut,
  RefreshCcw,
  Loader2,
  Search,
  X,
  CheckCircle2,
  Package,
  Download,
} from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../lib/api";
import { GRADE_DOT, PRODUCT_ORDER, PRODUCT_SHORT_LABEL, PRODUCT_PACK_LABEL, PICKUP_CODE, PICKUP_COLOR, PICKUP_BADGE_CLASS, formatAUD } from "../lib/products";

const TOKEN_KEY = "tnb_staff_token";
// Uses sessionStorage (cleared on browser close) instead of localStorage.
const tokenStore = {
  get: () => {
    try { return sessionStorage.getItem(TOKEN_KEY) || ""; } catch { return ""; }
  },
  set: (v) => {
    try { sessionStorage.setItem(TOKEN_KEY, v); } catch { /* storage disabled */ }
  },
  clear: () => {
    try { sessionStorage.removeItem(TOKEN_KEY); } catch { /* storage disabled */ }
    // Best-effort cleanup of any legacy localStorage token from earlier versions.
    try { localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
  },
};
const FILTERS = [
  { id: "all", label: "All" },
  { id: "paid", label: "Paid" },
  { id: "pending", label: "Pending" },
  { id: "picked_up", label: "Picked Up" },
];

export default function TnbStaff() {
  const [token, setToken] = useState(() => tokenStore.get());
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");

  const login = async () => {
    setLoading(true);
    try {
      const r = await apiClient.post("/staff/login", { password: pw });
      tokenStore.set(r.data.token);
      setToken(r.data.token);
      setPw("");
      toast.success("Signed in");
    } catch {
      toast.error("Invalid password");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    tokenStore.clear();
    setToken("");
    setOrders([]);
  };

  const loadOrders = async (q = "") => {
    setLoading(true);
    try {
      const r = await apiClient.get("/staff/orders", {
        params: q ? { q } : {},
        headers: { "X-Staff-Password": token },
      });
      setOrders(r.data.orders || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Session expired");
        logout();
      } else {
        toast.error("Failed to load orders");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) loadOrders();
  }, [token]);

  useEffect(() => {
    if (!token) return;
    const t = setTimeout(() => loadOrders(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  const filteredOrders = useMemo(() => {
    return orders.filter((o) => {
      if (filter === "all") return true;
      if (filter === "paid")
        return o.fulfilment_status !== "Picked Up" && o.fulfilment_status !== "Cancelled";
      if (filter === "pending") return (o.fulfilment_status || "Pending") === "Pending";
      if (filter === "picked_up") return o.fulfilment_status === "Picked Up";
      return true;
    });
  }, [orders, filter]);

  const counts = useMemo(() => {
    const c = { all: orders.length, paid: 0, pending: 0, picked_up: 0 };
    orders.forEach((o) => {
      const f = o.fulfilment_status || "Pending";
      if (f !== "Picked Up" && f !== "Cancelled") c.paid += 1;
      if (f === "Pending") c.pending += 1;
      if (f === "Picked Up") c.picked_up += 1;
    });
    return c;
  }, [orders]);

  const updateStatus = async (order_id, next) => {
    try {
      await apiClient.patch(
        `/staff/orders/${order_id}`,
        { fulfilment_status: next },
        { headers: { "X-Staff-Password": token } }
      );
      setOrders((prev) =>
        prev.map((o) => (o.order_id === order_id ? { ...o, fulfilment_status: next } : o))
      );
      toast.success(`${order_id} → ${next}`);
    } catch {
      toast.error("Failed to update");
    }
  };

  const [csvLoading, setCsvLoading] = useState(false);
  const downloadCsv = async () => {
    setCsvLoading(true);
    try {
      const r = await apiClient.get("/staff/orders.csv", {
        headers: { "X-Staff-Password": token },
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, "0");
      const dd = String(today.getDate()).padStart(2, "0");
      a.download = `quick-oil-orders-${yyyy}-${mm}-${dd}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Session expired");
        logout();
      } else {
        toast.error("Failed to download CSV");
      }
    } finally {
      setCsvLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-app text-app flex items-center justify-center p-6">
        <div className="max-w-sm w-full bg-elev border border-app rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-1">
            <LockKeyhole className="h-4 w-4 text-subtle-app" />
            <div className="text-[11px] uppercase tracking-[0.25em] text-subtle-app font-bold">
              TNB Staff
            </div>
          </div>
          <div className="text-[24px] font-extrabold tracking-tight mb-5">Sign in</div>
          <input
            data-testid="staff-password-input"
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && login()}
            placeholder="Staff password"
            className="w-full h-12 px-4 bg-elev-2 border border-app rounded-xl text-app placeholder:text-subtle-app"
          />
          <button
            data-testid="staff-login-button"
            onClick={login}
            disabled={loading || !pw}
            className="pressable mt-3 w-full bg-accent-app text-accent-fg bg-accent-hover-app rounded-full py-3 font-extrabold uppercase tracking-wider disabled:opacity-40"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin inline" /> : "Sign in"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-app text-app pb-10">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-app bg-app/90 backdrop-blur-md">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-subtle-app font-bold">
              TNB
            </div>
            <div className="text-[20px] font-extrabold tracking-tight leading-none mt-1">
              <span className="text-accent-app">Staff</span>
              <span className="ml-2 text-subtle-app font-mono tab-num text-[14px]">
                · {orders.length} orders
              </span>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => loadOrders(query.trim())}
              data-testid="staff-refresh"
              className="pressable inline-flex items-center gap-1.5 bg-elev border border-app rounded-full px-3 py-2 text-[12px] font-semibold"
            >
              <RefreshCcw className="h-3.5 w-3.5" /> Refresh
            </button>
            <button
              onClick={logout}
              data-testid="staff-logout"
              className="pressable inline-flex items-center gap-1.5 bg-elev border border-app rounded-full px-3 py-2 text-[12px] font-semibold"
            >
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-5">
        {/* Search */}
        <div>
          <div className="flex items-center justify-between mb-2 gap-3">
            <div className="text-[10px] uppercase tracking-[0.25em] text-subtle-app font-bold">
              Order Search
            </div>
            <button
              data-testid="staff-download-csv"
              onClick={downloadCsv}
              disabled={csvLoading}
              className="pressable inline-flex items-center gap-1.5 bg-elev border border-app hover:border-strong-app rounded-full px-3 py-1.5 text-[11px] font-extrabold uppercase tracking-wider disabled:opacity-50"
            >
              {csvLoading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Download className="h-3.5 w-3.5" />
              )}
              Download Orders CSV
            </button>
          </div>
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-subtle-app" />
            <input
              data-testid="staff-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search orders…"
              className="w-full h-12 pl-11 pr-11 bg-elev border border-app rounded-xl text-app placeholder:text-subtle-app"
            />
            {query && (
              <button
                data-testid="staff-search-clear"
                onClick={() => setQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full hover:bg-white/5 flex items-center justify-center"
              >
                <X className="h-3.5 w-3.5 text-subtle-app" />
              </button>
            )}
          </div>
          <div className="text-[11px] text-subtle-app mt-1.5">
            Search by order ID, customer, mobile, or business
          </div>
        </div>

        {/* Filter chips */}
        <div className="mt-4 -mx-5 px-5 overflow-x-auto no-scrollbar">
          <div className="flex items-center gap-2 min-w-max pb-1">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                data-testid={`filter-${f.id}`}
                onClick={() => setFilter(f.id)}
                className={`pressable inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-[12px] font-bold uppercase tracking-wider ${
                  filter === f.id
                    ? "bg-accent-app text-accent-fg border-accent-app"
                    : "bg-elev text-app border-app hover:border-strong-app"
                }`}
              >
                {f.label}
                <span
                  className={`text-[10px] font-mono tab-num px-1.5 py-0.5 rounded ${
                    filter === f.id ? "bg-black/15" : "bg-elev-2 text-muted-app"
                  }`}
                >
                  {counts[f.id]}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Orders list */}
        <div className="mt-5">
          {loading && orders.length === 0 ? (
            <div className="flex items-center gap-2 text-subtle-app py-16 justify-center">
              <Loader2 className="animate-spin h-4 w-4" /> Loading…
            </div>
          ) : filteredOrders.length === 0 ? (
            <EmptyState hasQuery={!!query} filter={filter} />
          ) : (
            <div className="space-y-3">
              {filteredOrders.map((o) => (
                <OrderCard key={o.order_id} order={o} onUpdate={updateStatus} />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function OrderCard({ order: o, onUpdate }) {
  const created = useMemo(() => {
    try {
      return new Date(o.created_at).toLocaleString("en-AU", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return o.created_at;
    }
  }, [o.created_at]);

  const fulfilment = o.fulfilment_status || "Pending";
  const isPickedUp = fulfilment === "Picked Up";
  const isCancelled = fulfilment === "Cancelled";

  return (
    <article
      data-testid={`staff-order-${o.order_id}`}
      className="bg-elev border border-app rounded-2xl p-4 sm:p-5"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-subtle-app font-bold">
            Order ID
          </div>
          <div className="font-mono tab-num text-[22px] font-extrabold tracking-tight text-accent-app mt-0.5">
            {o.order_id}
          </div>
          <div className="text-[11px] text-subtle-app mt-1">{created}</div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="emerald">PAID</Badge>
          <FulfilBadge status={fulfilment} />
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-3 text-[13px]">
        <Field label="Customer" value={o.customer_name} />
        <Field label="Mobile" value={o.mobile} />
        <Field label="Business" value={o.business_name} className="col-span-2 sm:col-span-1" />
        <Field label="Email" value={o.email} truncate />
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2">
        {PRODUCT_ORDER.map((pid) => {
          const key = pid === "superq_5w30_c3" ? "qty_5w30_c3" : pid === "superq_10w30" ? "qty_10w30" : "qty_10w40";
          const q = o[key] || 0;
          return (
            <div
              key={pid}
              className={`rounded-xl bg-elev-2 border border-app px-3 py-2 ${q === 0 ? "opacity-45" : ""}`}
            >
              <div className="flex items-center gap-1.5 flex-wrap">
                <span
                  className={`inline-flex items-center text-[9px] font-extrabold uppercase tracking-widest border rounded-full px-1.5 py-0.5 ${PICKUP_BADGE_CLASS[pid]}`}
                >
                  {PICKUP_CODE[pid]} / {PICKUP_COLOR[pid]}
                </span>
              </div>
              <div className="mt-1 text-[12px] font-semibold text-app truncate">
                {PRODUCT_SHORT_LABEL[pid]}
              </div>
              <div className="text-[10px] text-subtle-app truncate">
                {PRODUCT_PACK_LABEL[pid]}
              </div>
              <div className="mt-1 font-mono tab-num text-[16px] font-bold">×&nbsp;{q}</div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-app pt-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-subtle-app font-bold">
            Total Paid · GST inc
          </div>
          <div className="font-mono tab-num text-[22px] font-extrabold tracking-tight">
            {formatAUD(o.total)}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {!isPickedUp && !isCancelled && (
            <button
              data-testid={`mark-picked-up-${o.order_id}`}
              onClick={() => onUpdate(o.order_id, "Picked Up")}
              className="pressable inline-flex items-center gap-2 rounded-full bg-accent-app text-accent-fg bg-accent-hover-app px-4 py-2.5 text-[12px] font-extrabold uppercase tracking-wider"
            >
              <CheckCircle2 className="h-4 w-4" strokeWidth={3} />
              Mark as Picked Up
            </button>
          )}
          {isPickedUp && (
            <button
              data-testid={`revert-pending-${o.order_id}`}
              onClick={() => onUpdate(o.order_id, "Pending")}
              className="pressable inline-flex items-center gap-2 rounded-full bg-elev-2 border border-app hover:border-strong-app px-4 py-2.5 text-[12px] font-extrabold uppercase tracking-wider text-muted-app"
            >
              Revert to Pending
            </button>
          )}
          {!isCancelled && !isPickedUp && (
            <button
              data-testid={`cancel-${o.order_id}`}
              onClick={() => onUpdate(o.order_id, "Cancelled")}
              className="pressable rounded-full border border-app hover:border-red-500/60 hover:text-red-400 bg-elev-2 px-3 py-2.5 text-[12px] font-bold uppercase tracking-wider text-muted-app"
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </article>
  );
}

function Field({ label, value, truncate, className = "" }) {
  return (
    <div className={`${truncate ? "min-w-0" : ""} ${className}`}>
      <div className="text-[10px] uppercase tracking-widest text-subtle-app font-bold">
        {label}
      </div>
      <div className={`text-app ${truncate ? "truncate" : ""}`} title={value || ""}>
        {value || "-"}
      </div>
    </div>
  );
}

function Badge({ tone, children }) {
  const map = {
    emerald: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    amber: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    red: "bg-red-500/15 text-red-300 border-red-500/30",
    stone: "bg-white/5 text-muted-app border-app",
  };
  const dot = { emerald: "bg-emerald-400", amber: "bg-amber-400", red: "bg-red-400", stone: "bg-neutral-400" }[tone];
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-widest rounded-full px-2.5 py-1 border ${map[tone]}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
      {children}
    </span>
  );
}

function FulfilBadge({ status }) {
  const tone = status === "Picked Up" ? "emerald" : status === "Cancelled" ? "red" : "amber";
  return <Badge tone={tone}>{status}</Badge>;
}

function EmptyState({ hasQuery, filter }) {
  return (
    <div className="py-16 text-center">
      <div className="mx-auto h-12 w-12 rounded-full bg-elev border border-app flex items-center justify-center mb-3">
        <Package className="h-5 w-5 text-subtle-app" />
      </div>
      <div className="text-app font-semibold">
        {hasQuery ? "No orders match your search." : filter === "all" ? "No paid orders yet." : "Nothing in this filter."}
      </div>
      <div className="text-subtle-app text-sm mt-1">
        Orders appear here once Stripe confirms payment.
      </div>
    </div>
  );
}
